import React, { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';
import Navbar from '../components/Navbar';

const NAVY = '#1A2B4C';
const GOLD = '#FCD359';
const GOLD_DARK = '#E0B73D';
const DANGER = '#DC2626';

const ROLE_COLORS = {
  student: '#0EA5E9',
  teacher: '#16A34A',
  institution_admin: '#9333EA',
  developer: NAVY,
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [realCounts, setRealCounts] = useState({});
  const [totalUsers, setTotalUsers] = useState(0);
  const [consentCount, setConsentCount] = useState(0);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [simulatedFilter, setSimulatedFilter] = useState('real'); // default: real only
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const qs = new URLSearchParams({ limit: '500' });
      if (roleFilter) qs.set('role', roleFilter);
      if (search) qs.set('search', search);
      if (simulatedFilter) qs.set('simulated', simulatedFilter);
      const [u, a] = await Promise.all([
        api.get(`/api/admin/users?${qs.toString()}`),
        api.get('/api/admin/users/audit?limit=30'),
      ]);
      setUsers(u.data?.users || []);
      setCounts(u.data?.role_counts || {});
      setRealCounts(u.data?.real_counts || {});
      setTotalUsers(u.data?.total_users || 0);
      setConsentCount(u.data?.consent_count || 0);
      setAudit(a.data?.audit || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => { if (!cancel) await load(); })();
    return () => { cancel = true; };
  }, [roleFilter, simulatedFilter]);

  const onSearchSubmit = (e) => {
    e?.preventDefault();
    load();
  };

  const changeRole = async (email, newRole) => {
    setBusy(`role-${email}`);
    try {
      await api.patch(`/api/admin/users/${encodeURIComponent(email)}/role`, { role: newRole });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Role change failed.');
    } finally {
      setBusy('');
    }
  };

  const doDelete = async (email) => {
    setBusy(`del-${email}`);
    try {
      await api.delete(`/api/admin/users/${encodeURIComponent(email)}`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--gr-bone-2)' }}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-6" data-testid="users-page">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: NAVY }} data-testid="users-title">User Management</h1>
            <div className="text-sm text-gray-600 mt-1 max-w-2xl">
              List, search, change roles, or delete registered users. Audit-logged.
              Cannot delete yourself or the last remaining developer (lock-out safety).
            </div>
          </div>
        </div>

        {/* Role count KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Kpi label="Total / Real" value={`${totalUsers} / ${Object.values(realCounts).reduce((a,b)=>a+(b||0),0)}`} accent={NAVY} />
          <Kpi label="Students (real)" value={`${counts.student || 0} / ${realCounts.student || 0}`} accent={ROLE_COLORS.student} />
          <Kpi label="Teachers (real)" value={`${counts.teacher || 0} / ${realCounts.teacher || 0}`} accent={ROLE_COLORS.teacher} />
          <Kpi label="Inst. admins (real)" value={`${counts.institution_admin || 0} / ${realCounts.institution_admin || 0}`} accent={ROLE_COLORS.institution_admin} />
          <Kpi label="Research consent" value={consentCount} accent={GOLD_DARK} />
        </div>

        {/* Filters */}
        <div className="card p-4 mb-4">
          <form onSubmit={onSearchSubmit} className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">
                Search by email or name
              </label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="e.g. jane@example.com"
                className="input-field"
                data-testid="users-search-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">Role</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="input-field py-2 px-3 text-sm"
                data-testid="users-role-filter"
              >
                <option value="">All roles</option>
                <option value="student">Students</option>
                <option value="teacher">Teachers</option>
                <option value="institution_admin">Institution admins</option>
                <option value="developer">Developers</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">Data</label>
              <select
                value={simulatedFilter}
                onChange={(e) => setSimulatedFilter(e.target.value)}
                className="input-field py-2 px-3 text-sm"
                data-testid="users-simulated-filter"
              >
                <option value="real">Real only</option>
                <option value="simulated">Simulated only</option>
                <option value="all">Include simulated</option>
              </select>
            </div>
            <button type="submit" className="btn-primary text-sm" data-testid="users-search-btn">Search</button>
          </form>
        </div>

        {/* Users table */}
        <div className="card p-4 mb-4">
          <div className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>
            Users ({users.length})
          </div>
          {loading ? (
            <div className="p-6 text-center text-gray-500" data-testid="users-loading">Loading…</div>
          ) : err ? (
            <div className="p-4 text-red-600">{err}</div>
          ) : users.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No users match.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                    <th className="pb-2">Public ID</th>
                    <th className="pb-2">Name</th>
                    <th className="pb-2">Email</th>
                    <th className="pb-2">Role</th>
                    <th className="pb-2 text-center">Type</th>
                    <th className="pb-2 text-center">Research</th>
                    <th className="pb-2">Joined</th>
                    <th className="pb-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.email}
                        className="border-t"
                        style={{ borderColor: 'var(--gr-border)' }}
                        data-testid={`user-row-${u.email}`}>
                      <td className="py-2 text-xs font-mono font-bold" style={{ color: NAVY }}>
                        {u.public_id || '—'}
                      </td>
                      <td className="py-2">{u.name || '—'}</td>
                      <td className="py-2 text-xs font-mono">{u.email}</td>
                      <td className="py-2">
                        <select
                          value={u.role}
                          onChange={(e) => changeRole(u.email, e.target.value)}
                          disabled={busy === `role-${u.email}`}
                          className="text-xs font-bold uppercase px-2 py-1 rounded border"
                          style={{
                            background: `${ROLE_COLORS[u.role] || NAVY}15`,
                            color: ROLE_COLORS[u.role] || NAVY,
                            borderColor: ROLE_COLORS[u.role] || NAVY,
                          }}
                          data-testid={`role-select-${u.email}`}
                        >
                          <option value="student">student</option>
                          <option value="teacher">teacher</option>
                          <option value="institution_admin">institution_admin</option>
                          <option value="developer">developer</option>
                        </select>
                      </td>
                      <td className="py-2 text-center">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded"
                              style={{
                                background: u.is_simulated ? '#E5E7EB' : '#D1FAE5',
                                color: u.is_simulated ? '#6B7280' : '#065F46',
                              }}>
                          {u.is_simulated ? 'sim' : 'real'}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span title={u.research_consent_at ? `Consented ${u.research_consent_at.slice(0,10)}` : 'No research consent'}>
                          {u.research_consent ? '✓' : '—'}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-gray-500">{(u.created_at || '').slice(0, 10) || '—'}</td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => setConfirmDelete(u)}
                          disabled={busy === `del-${u.email}`}
                          className="text-xs px-3 py-1 rounded border font-semibold"
                          style={{ color: DANGER, borderColor: DANGER, background: 'rgba(220,38,38,0.05)' }}
                          data-testid={`delete-btn-${u.email}`}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Audit log */}
        <div className="card p-4">
          <div className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>
            Recent admin actions
          </div>
          {audit.length === 0 ? (
            <div className="text-gray-500 text-sm">No admin actions yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="pb-2">When</th>
                  <th className="pb-2">Action</th>
                  <th className="pb-2">Actor</th>
                  <th className="pb-2">Target</th>
                  <th className="pb-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: 'var(--gr-border)' }}>
                    <td className="py-2 text-xs">{(a.at || '').slice(0, 16).replace('T', ' ')} UTC</td>
                    <td className="py-2">
                      <span className="text-xs font-bold uppercase px-2 py-0.5 rounded"
                            style={{
                              background: a.action === 'delete' ? '#FEE2E2' : '#FEF3C7',
                              color: a.action === 'delete' ? '#B91C1C' : '#92400E',
                            }}>
                        {a.action}
                      </span>
                    </td>
                    <td className="py-2 text-xs font-mono">{a.actor}</td>
                    <td className="py-2 text-xs font-mono">{a.target}</td>
                    <td className="py-2 text-xs text-gray-500">
                      {a.action === 'role_change' && (<span>{a.from_role} → {a.to_role}</span>)}
                      {a.action === 'delete' && (<span>{a.target_name || ''} ({a.target_role})</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
             onClick={() => setConfirmDelete(null)}
             data-testid="delete-confirm-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="card max-w-md w-full p-5">
            <div className="text-lg font-bold mb-2" style={{ color: DANGER }}>Delete this user?</div>
            <div className="text-sm text-gray-600 mb-4">
              <div className="mb-1"><b>{confirmDelete.name || '—'}</b></div>
              <div className="font-mono text-xs">{confirmDelete.email}</div>
              <div className="text-xs mt-1">Role: <b>{confirmDelete.role}</b></div>
              <div className="text-xs mt-3 text-gray-500">
                This removes their login. Their past discussion replies and pathway logs remain
                attributed to this email for analytics history.
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="btn-outline text-sm"
                      data-testid="delete-cancel-btn">
                Cancel
              </button>
              <button onClick={() => doDelete(confirmDelete.email)}
                      disabled={busy.startsWith('del-')}
                      style={{ background: DANGER, color: '#FFFFFF', border: `1px solid ${DANGER}` }}
                      className="text-sm font-bold px-4 py-2 rounded"
                      data-testid="delete-confirm-btn">
                {busy === `del-${confirmDelete.email}` ? 'Deleting…' : 'Delete user'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, accent }) => (
  <div className="card p-4">
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="text-2xl font-bold mt-1" style={{ color: accent || NAVY }}>{value}</div>
  </div>
);

export default UsersPage;
